import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  LinearProgress,
  Chip,
  ThemeProvider,
  CssBaseline,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { 
  Download as DownloadIcon, 
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useAuth0 } from "@auth0/auth0-react";
import theme from "../theme";
import QueueAnalyticsApi, { Queue, QueueAnalytics as QueueAnalyticsData } from "../apis/queueAnalyticsApi";
import researchReportApi, { ResearchReport } from "../apis/researchReportApi";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import { getTicketScopeLabel, getTicketScopeLabelPlural } from "../utils/mspUtils";

interface TimeframeOption {
  label: string;
  value: string;
  days: number | null;
}

const timeframeOptions: TimeframeOption[] = [
  { label: "Last 1 day", value: "1d", days: 1 },
  { label: "Last 7 days", value: "7d", days: 7 },
  { label: "Last 30 days", value: "30d", days: 30 },
  { label: "Last 90 days", value: "90d", days: 90 },
  { label: "Last 1 year", value: "1y", days: 365 },
  { label: "All time", value: "all", days: null },
];

const formatTimeDuration = (hours: number): string => {
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes}m`;
  } else if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  } else {
    const days = hours / 24;
    return `${days.toFixed(1)}d`;
  }
};

const QueueAnalytics: React.FC = () => {
  const exportRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth0();
  
  // State
  const [queues, setQueues] = useState<Queue[]>([]);
  const [selectedQueue, setSelectedQueue] = useState<number | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>("30d");
  const [analytics, setAnalytics] = useState<QueueAnalyticsData | null>(null);
  const [savedReports, setSavedReports] = useState<ResearchReport[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Loading states
  const [loadingQueues, setLoadingQueues] = useState(false);
  const [computingAnalytics, setComputingAnalytics] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Error states
  const [error, setError] = useState<string | null>(null);
  const [mspType, setMspType] = useState<string | null>(null);
  const scopeLabel = getTicketScopeLabel(mspType);
  const scopeLabelPlural = getTicketScopeLabelPlural(mspType);

  // Load queues and reports on mount
  useEffect(() => {
    loadQueues();
    loadSavedReports();
  }, []);

  // Set page title
  useEffect(() => {
    document.title = `${scopeLabel} Analytics - Theo`;
    return () => {
      document.title = "Theo";
    };
  }, [scopeLabel]);

  const loadQueues = async () => {
    try {
      setLoadingQueues(true);
      setError(null);
      const response = await QueueAnalyticsApi.getQueues();
      setQueues(response.queues);
      setMspType(response.msp_type);
    } catch (err: any) {
      console.error("Error loading queues:", err);
      setError(err.message || "Failed to load queues");
    } finally {
      setLoadingQueues(false);
    }
  };

  const handleComputeAnalytics = async () => {
    if (!selectedTimeframe) {
      setError("Please select a timeframe");
      return;
    }

    try {
      setComputingAnalytics(true);
      setError(null);
      setProgress(10);
      
      // Calculate dates
      const endDate = new Date().toISOString();
      const timeframe = timeframeOptions.find(t => t.value === selectedTimeframe);
      let startDate: string | null = null;
      
      if (timeframe && timeframe.days !== null) {
        const start = new Date();
        start.setDate(start.getDate() - timeframe.days);
        startDate = start.toISOString();
      }
      
      setProgress(30);
      
      // Compute analytics
      const result = await QueueAnalyticsApi.computeAnalytics({
        queue_id: selectedQueue,
        start_date: startDate,
        end_date: endDate,
        skip_classification: false,
      });
      
      setProgress(100);
      setAnalytics(result);
      setSelectedReportId(null); // Clear selected report when computing new
      
      // Auto-save the report
      await saveReportAutomatically(result, startDate, endDate);
    } catch (err: any) {
      console.error("Error computing analytics:", err);
      setError(err.message || "Failed to compute analytics");
    } finally {
      setComputingAnalytics(false);
      setProgress(0);
    }
  };

  const saveReportAutomatically = async (analyticsData: QueueAnalyticsData, startDate: string | null, endDate: string) => {
    if (!user?.email) return;
    
    try {
      setSavingReport(true);
      const queueName = selectedQueue 
        ? queues.find(q => q.id === selectedQueue)?.name || null
        : null;
      const timeframe = timeframeOptions.find(t => t.value === selectedTimeframe)?.label || null;
      
      await researchReportApi.saveReport({
        queue_id: selectedQueue,
        queue_name: queueName,
        timeframe: timeframe,
        start_date: startDate,
        end_date: endDate,
        analytics_data: analyticsData,
      });
      
      // Reload reports list
      await loadSavedReports();
    } catch (err: any) {
      console.error("Error auto-saving report:", err);
      // Don't show error to user for auto-save failures
    } finally {
      setSavingReport(false);
    }
  };

  const loadSavedReports = async () => {
    try {
      setLoadingReports(true);
      const response = await researchReportApi.getReports();
      setSavedReports(response.reports);
    } catch (err: any) {
      console.error("Error loading saved reports:", err);
      // Don't show error, just log it
    } finally {
      setLoadingReports(false);
    }
  };

  const handleSelectReport = async (reportId: number) => {
    try {
      setLoadingReport(true);
      setError(null);
      setSelectedReportId(reportId);
      
      const report = await researchReportApi.getReportById(reportId);
      
      // Set analytics data from saved report
      setAnalytics(report.analytics_data);
      
      // Update UI to match saved report
      if (report.queue_id) {
        setSelectedQueue(report.queue_id);
      }
      if (report.timeframe) {
        const matchingTimeframe = timeframeOptions.find(t => t.label === report.timeframe);
        if (matchingTimeframe) {
          setSelectedTimeframe(matchingTimeframe.value);
        }
      }
    } catch (err: any) {
      console.error("Error loading report:", err);
      setError(err.message || "Failed to load saved report");
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDeleteReport = async (reportId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm("Are you sure you want to delete this report?")) {
      return;
    }
    
    try {
      await researchReportApi.deleteReport(reportId);
      await loadSavedReports();
      
      // If deleted report was selected, clear it
      if (selectedReportId === reportId) {
        setSelectedReportId(null);
        setAnalytics(null);
      }
    } catch (err: any) {
      console.error("Error deleting report:", err);
      setError(err.message || "Failed to delete report");
    }
  };

  /** Saved report timestamps: browser local timezone + short TZ label (server sends UTC ISO). */
  const formatReportDate = (dateString: string) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    try {
      return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      }).format(date);
    } catch {
      return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    }
  };

  const handleExportPDF = async () => {
    if (!analytics || !exportRef.current) {
      setError("No analytics data to export");
      return;
    }

    try {
      setExportingPdf(true);
      setError(null);

      // Get the element to export
      const element = exportRef.current;
      
      // Create canvas from the element
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#F8F9FA",
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      
      // Calculate PDF dimensions
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = (imgHeight * pdfWidth) / imgWidth;
      
      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");
      
      // Add image to PDF
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      
      // Generate filename
      const queueName = selectedQueue 
        ? queues.find(q => q.id === selectedQueue)?.name || `${scopeLabel}-${selectedQueue}`
        : `All-${scopeLabelPlural}`;
      const timeframe = timeframeOptions.find(t => t.value === selectedTimeframe)?.label || selectedTimeframe;
      const dateStr = new Date().toISOString().split("T")[0];
      const filename = `${scopeLabel}-Analytics-${queueName}-${timeframe}-${dateStr}.pdf`;
      
      // Save PDF
      pdf.save(filename);
    } catch (err: any) {
      console.error("Error exporting PDF:", err);
      setError(err.message || "Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  // Check if MSP type is not Autotask, Halo, or ConnectWise
  if (mspType && mspType !== "autotask" && mspType !== "halo" && mspType !== "connectwise") {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            background: theme.palette.background.default,
            minHeight: "100vh",
            maxWidth: "95%",
            width: "100%",
            mx: "auto",
          }}
        >
          <Alert severity="info">
            Queue Analytics is currently only available for Autotask, Halo, and ConnectWise. Your MSP is using {mspType}.
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          background: theme.palette.background.default,
        }}
      >
        {/* Sidebar - Saved Reports */}
        <Box
          sx={{
            width: sidebarOpen ? "320px" : "0",
            minWidth: sidebarOpen ? "320px" : "0",
            borderRight: sidebarOpen ? "1px solid #E2E8F0" : "none",
            background: "#FFFFFF",
            transition: "width 0.3s ease",
            overflow: "hidden",
            display: { xs: sidebarOpen ? "block" : "none", md: "block" },
            position: { xs: "fixed", md: "relative" },
            height: { xs: "100vh", md: "auto" },
            zIndex: { xs: 1200, md: "auto" },
            overflowY: "auto",
          }}
        >
          {sidebarOpen && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: theme.palette.custom.titleGray,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <HistoryIcon fontSize="small" />
                  Saved Reports
                </Typography>
                <Button
                  size="small"
                  onClick={() => setSidebarOpen(false)}
                  sx={{ display: { md: "none" }, minWidth: "auto", p: 0.5 }}
                >
                  ×
                </Button>
              </Box>

              {loadingReports ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : savedReports.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                  No saved reports yet. Generate a report to save it automatically.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {savedReports.map((report) => (
                    <Card
                      key={report.id}
                      onClick={() => handleSelectReport(report.id)}
                      sx={{
                        cursor: "pointer",
                        border: selectedReportId === report.id ? "2px solid" : "1px solid",
                        borderColor: selectedReportId === report.id 
                          ? theme.palette.custom.theoAI 
                          : "#E2E8F0",
                        background: selectedReportId === report.id 
                          ? "#F0F9FF" 
                          : "#FFFFFF",
                        borderRadius: "6px",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: theme.palette.custom.theoAI,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.custom.titleGray,
                              flex: 1,
                              mr: 1,
                            }}
                          >
                            {report.report_name}
                          </Typography>
                          <Button
                            size="small"
                            onClick={(e) => handleDeleteReport(report.id, e)}
                            sx={{
                              minWidth: "auto",
                              p: 0.5,
                              color: "#EF4444",
                              "&:hover": {
                                background: "#FEE2E2",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          {report.queue_name || `All ${scopeLabelPlural}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          {report.timeframe || "Custom"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatReportDate(report.created_at)}
                        </Typography>
                        {loadingReport && selectedReportId === report.id && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                            <CircularProgress size={12} />
                            <Typography variant="caption" color="text.secondary">
                              Loading...
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3, md: 4 },
            background: theme.palette.background.default,
            minHeight: "100vh",
            maxWidth: sidebarOpen ? "calc(100% - 320px)" : "100%",
            width: "100%",
            mx: "auto",
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Button
                size="small"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                sx={{
                  minWidth: "auto",
                  p: 1,
                  display: { md: "none" },
                }}
              >
                <HistoryIcon />
              </Button>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.custom.titleGray,
                }}
              >
                {scopeLabel} Analytics
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: "#64748B",
              }}
            >
              {`Analyze ticket ${scopeLabelPlural.toLowerCase()} with AI-powered insights`}
            </Typography>
            {selectedReportId && (
              <Chip
                label={`Viewing saved report`}
                size="small"
                sx={{
                  mt: 1,
                  background: "#DBEAFE",
                  color: "#1E40AF",
                }}
              />
            )}
            {savingReport && (
              <Chip
                label="Saving report..."
                size="small"
                sx={{
                  mt: 1,
                  ml: 1,
                  background: "#D1FAE5",
                  color: "#065F46",
                }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {!sidebarOpen && (
              <Button
                size="small"
                onClick={() => setSidebarOpen(true)}
                sx={{
                  minWidth: "auto",
                  display: { xs: "none", md: "flex" },
                }}
              >
                <HistoryIcon />
              </Button>
            )}
            {analytics && (
              <Button
                variant="contained"
                startIcon={exportingPdf ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={handleExportPDF}
                disabled={exportingPdf}
                sx={{
                  background: theme.palette.custom.theoAI,
                  "&:hover": {
                    background: theme.palette.custom.theoAI,
                    opacity: 0.9,
                  },
                }}
              >
                {exportingPdf ? "Exporting..." : "Export PDF"}
              </Button>
            )}
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Configuration Card */}
        <Card
          sx={{
            mb: 3,
            background: "#FFFFFF",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            border: "1px solid #F1F5F9",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontSize: "16px",
                fontWeight: 600,
                color: theme.palette.custom.titleGray,
              }}
            >
              Configure Analysis
            </Typography>

            <Grid container spacing={3}>
              {/* Queue/Board Selection */}
              <Grid item xs={12} md={4}>
                <LayeredAutocompleteSelect
                  label={scopeLabel}
                  value={selectedQueue == null ? "all" : String(selectedQueue)}
                  onChange={(nextValue) =>
                    setSelectedQueue(nextValue === "all" ? null : Number(nextValue))
                  }
                  disabled={loadingQueues || computingAnalytics}
                  disableClearable
                  options={[
                    { value: "all", label: `All ${scopeLabelPlural}` },
                    ...queues.map((queue) => ({
                      value: String(queue.id),
                      label: queue.name,
                    })),
                  ]}
                />
              </Grid>

              {/* Timeframe Selection */}
              <Grid item xs={12} md={4}>
                <LayeredAutocompleteSelect
                  label="Timeframe"
                  value={selectedTimeframe}
                  onChange={setSelectedTimeframe}
                  disabled={computingAnalytics}
                  disableClearable
                  options={timeframeOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                />
              </Grid>

              {/* Compute Button */}
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleComputeAnalytics}
                  disabled={loadingQueues || computingAnalytics}
                  sx={{
                    height: "56px",
                    background: theme.palette.custom.theoAI,
                    "&:hover": {
                      background: theme.palette.custom.theoAI,
                      opacity: 0.9,
                    },
                  }}
                >
                  {computingAnalytics ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                      Computing...
                    </>
                  ) : (
                    "Compute Analytics"
                  )}
                </Button>
              </Grid>
            </Grid>

            {/* Progress Bar */}
            {computingAnalytics && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography
                  variant="body2"
                  sx={{ mt: 1, textAlign: "center", color: "#64748B" }}
                >
                  Analyzing tickets and computing metrics...
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Analytics Results */}
        {analytics && (
          <Box ref={exportRef}>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Total Tickets */}
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "#FFFFFF",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #F1F5F9",
                  }}
                >
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Total Tickets
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      {analytics.total_tickets}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Open Tickets */}
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "#FFFFFF",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #F1F5F9",
                  }}
                >
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Open Tickets
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: "#3B82F6" }}>
                      {analytics.time_metrics.open_tickets}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {analytics.total_tickets > 0
                        ? `${((analytics.time_metrics.open_tickets / analytics.total_tickets) * 100).toFixed(1)}%`
                        : "0%"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Past Due */}
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "#FFFFFF",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #F1F5F9",
                  }}
                >
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Past Due
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: "#EF4444" }}>
                      {analytics.time_metrics.past_due_tickets}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {analytics.total_tickets > 0
                        ? `${((analytics.time_metrics.past_due_tickets / analytics.total_tickets) * 100).toFixed(1)}%`
                        : "0%"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Total Hours */}
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "#FFFFFF",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #F1F5F9",
                  }}
                >
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Total Hours Worked
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: "#10B981" }}>
                      {analytics.time_metrics.total_hours_worked.toFixed(1)}h
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {analytics.time_metrics.tickets_with_hours} tickets
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts Row 1 */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Tickets by Priority */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: "#FFFFFF",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #F1F5F9",
                    minHeight: 400,
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontSize: "16px",
                        fontWeight: 600,
                        color: theme.palette.custom.titleGray,
                      }}
                    >
                      Tickets by Priority
                    </Typography>
                    {Object.keys(analytics.tickets_by_priority).length > 0 ? (
                      <PieChart
                        series={[
                          {
                            data: Object.entries(analytics.tickets_by_priority).map(([key, value], index) => ({
                              id: index,
                              value: value,
                              label: key,
                            })),
                            highlightScope: { faded: "global", highlighted: "item" },
                            faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                          },
                        ]}
                        height={300}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                        No priority data available
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Tickets by Status */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: "#FFFFFF",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #F1F5F9",
                    minHeight: 400,
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontSize: "16px",
                        fontWeight: 600,
                        color: theme.palette.custom.titleGray,
                      }}
                    >
                      Tickets by Status
                    </Typography>
                    {Object.keys(analytics.tickets_by_status).length > 0 ? (
                      <BarChart
                        xAxis={[
                          {
                            scaleType: "band",
                            data: Object.keys(analytics.tickets_by_status),
                            tickLabelStyle: {
                              angle: -45,
                              textAnchor: "end",
                              fontSize: 11,
                            },
                          },
                        ]}
                        series={[
                          {
                            data: Object.values(analytics.tickets_by_status),
                            color: theme.palette.custom.chartOrange,
                          },
                        ]}
                        height={300}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                        No status data available
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* AI-Categorized Issue Types */}
            {Object.keys(analytics.tickets_by_issue_type).length > 0 && (
              <Card
                sx={{
                  mb: 3,
                  background: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  border: "1px solid #F1F5F9",
                  position: "relative",
                }}
              >
                <Chip
                  label="AI Powered"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 20,
                    right: 20,
                    background: theme.palette.custom.theoAI,
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                />
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontSize: "16px",
                      fontWeight: 600,
                      color: theme.palette.custom.titleGray,
                    }}
                  >
                    Tickets by Issue Type (AI Categorized)
                  </Typography>
                  <BarChart
                    xAxis={[
                      {
                        scaleType: "band",
                        data: Object.keys(analytics.tickets_by_issue_type),
                        tickLabelStyle: {
                          angle: -45,
                          textAnchor: "end",
                          fontSize: 11,
                        },
                      },
                    ]}
                    series={[
                      {
                        data: Object.values(analytics.tickets_by_issue_type),
                        color: theme.palette.custom.theoAI,
                      },
                    ]}
                    height={400}
                  />
                  
                  {/* Confidence Breakdown */}
                  <Box sx={{ mt: 3, p: 2, background: "#F8FAFC", borderRadius: "6px" }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      AI Classification Confidence:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Chip
                        label={`High: ${analytics.confidence_breakdown.high}`}
                        size="small"
                        sx={{ background: "#10B981", color: "white" }}
                      />
                      <Chip
                        label={`Medium: ${analytics.confidence_breakdown.medium}`}
                        size="small"
                        sx={{ background: "#F59E0B", color: "white" }}
                      />
                      <Chip
                        label={`Low: ${analytics.confidence_breakdown.low}`}
                        size="small"
                        sx={{ background: "#EF4444", color: "white" }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Time Metrics */}
            <Card
              sx={{
                mb: 3,
                background: "#FFFFFF",
                borderRadius: "10px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                border: "1px solid #F1F5F9",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontSize: "16px",
                    fontWeight: 600,
                    color: theme.palette.custom.titleGray,
                  }}
                >
                  Time-Based Metrics
                </Typography>

                <Grid container spacing={3}>
                  {/* First Response Time */}
                  {analytics.time_metrics.tickets_with_first_response > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          background: "#F0F9FF",
                          borderRadius: "6px",
                          border: "1px solid #DBEAFE",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          First Response Time
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#3B82F6", mb: 1 }}>
                          {formatTimeDuration(analytics.time_metrics.first_response_avg_hours)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Average • {analytics.time_metrics.tickets_with_first_response} tickets
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Median: {formatTimeDuration(analytics.time_metrics.first_response_median_hours)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Resolution Time */}
                  {analytics.time_metrics.tickets_resolved > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          background: "#F0FDF4",
                          borderRadius: "6px",
                          border: "1px solid #DCFCE7",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Resolution Time
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#10B981", mb: 1 }}>
                          {formatTimeDuration(analytics.time_metrics.resolution_avg_hours)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Average • {analytics.time_metrics.tickets_resolved} tickets
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Median: {formatTimeDuration(analytics.time_metrics.resolution_median_hours)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {/* Hours Worked */}
                  {analytics.time_metrics.tickets_with_hours > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          p: 2,
                          background: "#FEF3C7",
                          borderRadius: "6px",
                          border: "1px solid #FDE68A",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Hours Worked
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#F59E0B", mb: 1 }}>
                          {analytics.time_metrics.hours_worked_avg.toFixed(1)}h
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Average • {analytics.time_metrics.tickets_with_hours} tickets
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Total: {analytics.time_metrics.total_hours_worked.toFixed(1)}h
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            {analytics.category_metrics && Object.keys(analytics.category_metrics).length > 0 ? (
              <>
                {/* Tickets by Category Chart */}
                <Card
                  sx={{
                    mb: 3,
                    background: "#FFFFFF",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #F1F5F9",
                    position: "relative",
                  }}
                >
                  <Chip
                    label="AI Categorized"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      background: theme.palette.custom.theoAI,
                      color: "#FFFFFF",
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  />
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontSize: "16px",
                        fontWeight: 600,
                        color: theme.palette.custom.titleGray,
                      }}
                    >
                      Tickets by Category
                    </Typography>
                    <BarChart
                      xAxis={[
                        {
                          scaleType: "band",
                          data: Object.keys(analytics.category_metrics),
                          tickLabelStyle: {
                            angle: -45,
                            textAnchor: "end",
                            fontSize: 10,
                          },
                        },
                      ]}
                      series={[
                        {
                          data: Object.values(analytics.category_metrics).map((m) => m.total_tickets),
                          color: theme.palette.custom.theoAI,
                          label: "Total Tickets",
                        },
                      ]}
                      height={400}
                    />
                  </CardContent>
                </Card>

                {/* Category Metrics Breakdown */}
                <Card
                  sx={{
                    mb: 3,
                    background: "#FFFFFF",
                    borderRadius: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    border: "1px solid #F1F5F9",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        fontSize: "16px",
                        fontWeight: 600,
                        color: theme.palette.custom.titleGray,
                      }}
                    >
                      Category Performance Metrics
                    </Typography>
                    
                    {/* Category Metrics Table */}
                    <Box sx={{ overflowX: "auto" }}>
                      <Box
                        component="table"
                        sx={{
                          width: "100%",
                          borderCollapse: "collapse",
                          "& th, & td": {
                            border: "1px solid #E2E8F0",
                            padding: "12px",
                            textAlign: "left",
                          },
                          "& th": {
                            background: "#F8FAFC",
                            fontWeight: 600,
                            fontSize: "12px",
                            color: theme.palette.custom.titleGray,
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          },
                          "& tr:nth-of-type(even)": {
                            background: "#FAFBFC",
                          },
                          "& tr:hover": {
                            background: "#F0F9FF",
                          },
                        }}
                      >
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Total Tickets</th>
                            <th>Open</th>
                            <th>Resolved</th>
                            <th>Past Due</th>
                            <th>Avg First Response</th>
                            <th>Avg Resolution</th>
                            <th>Total Hours</th>
                            <th>Avg Hours/Ticket</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(analytics.category_metrics)
                            .sort(([, a], [, b]) => b.total_tickets - a.total_tickets)
                            .map(([category, metrics]) => (
                              <tr key={category}>
                                <td>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {category}
                                  </Typography>
                                </td>
                                <td>
                                  <Chip
                                    label={metrics.total_tickets}
                                    size="small"
                                    sx={{
                                      background: "#E0E7FF",
                                      color: "#4338CA",
                                      fontWeight: 600,
                                    }}
                                  />
                                </td>
                                <td>
                                  <Typography variant="body2" sx={{ color: "#3B82F6" }}>
                                    {metrics.open_tickets}
                                  </Typography>
                                </td>
                                <td>
                                  <Typography variant="body2" sx={{ color: "#10B981" }}>
                                    {metrics.resolved_tickets}
                                  </Typography>
                                </td>
                                <td>
                                  <Typography variant="body2" sx={{ color: "#EF4444" }}>
                                    {metrics.past_due_tickets}
                                  </Typography>
                                </td>
                                <td>
                                  <Typography variant="body2">
                                    {metrics.tickets_with_first_response > 0
                                      ? formatTimeDuration(metrics.first_response_avg_hours)
                                      : "N/A"}
                                  </Typography>
                                  {metrics.tickets_with_first_response > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      ({metrics.tickets_with_first_response} tickets)
                                    </Typography>
                                  )}
                                </td>
                                <td>
                                  <Typography variant="body2">
                                    {metrics.resolved_tickets > 0
                                      ? formatTimeDuration(metrics.resolution_avg_hours)
                                      : "N/A"}
                                  </Typography>
                                </td>
                                <td>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#F59E0B" }}>
                                    {metrics.total_hours_worked.toFixed(1)}h
                                  </Typography>
                                </td>
                                <td>
                                  <Typography variant="body2">
                                    {metrics.tickets_with_hours > 0
                                      ? `${metrics.hours_worked_avg.toFixed(1)}h`
                                      : "N/A"}
                                  </Typography>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </Box>
                    </Box>

                    {/* Summary Stats */}
                    <Box sx={{ mt: 3, p: 2, background: "#F8FAFC", borderRadius: "6px" }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        Category Summary:
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Chip
                          label={`${Object.keys(analytics.category_metrics).length} Categories`}
                          size="small"
                          sx={{ background: "#DBEAFE", color: "#1E40AF" }}
                        />
                        <Chip
                          label={`${Object.values(analytics.category_metrics).reduce((sum, m) => sum + m.total_tickets, 0)} Total Tickets`}
                          size="small"
                          sx={{ background: "#D1FAE5", color: "#065F46" }}
                        />
                        <Chip
                          label={`${Object.values(analytics.category_metrics).reduce((sum, m) => sum + m.total_hours_worked, 0).toFixed(1)}h Total Hours`}
                          size="small"
                          sx={{ background: "#FEF3C7", color: "#92400E" }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </>
            ) : analytics.tickets_by_issue_type && Object.keys(analytics.tickets_by_issue_type).length > 0 ? (
              <Card
                sx={{
                  mb: 3,
                  background: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  border: "1px solid #F1F5F9",
                }}
              >
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Category metrics are being calculated. Please refresh or recompute analytics to see detailed category breakdown with time-based metrics.
                  </Alert>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontSize: "16px",
                      fontWeight: 600,
                      color: theme.palette.custom.titleGray,
                    }}
                  >
                    Tickets by Category (Basic View)
                  </Typography>
                  <Box>
                    {Object.entries(analytics.tickets_by_issue_type)
                      .sort(([, a], [, b]) => b - a)
                      .map(([category, count]) => (
                        <Box
                          key={category}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1.5,
                            borderBottom: "1px solid #F1F5F9",
                            "&:last-child": { borderBottom: "none" },
                          }}
                        >
                          <Typography variant="body2">{category}</Typography>
                          <Chip
                            label={count}
                            size="small"
                            sx={{
                              background: "#E0E7FF",
                              color: "#4338CA",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      ))}
                  </Box>
                </CardContent>
              </Card>
            ) : null}

            {/* Hierarchical Category & Subcategory Breakdown */}
            {analytics.tickets_by_category_and_subcategory && Object.keys(analytics.tickets_by_category_and_subcategory).length > 0 && (
              <Card
                sx={{
                  mb: 3,
                  background: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  border: "1px solid #F1F5F9",
                  position: "relative",
                }}
              >
                <Chip
                  label="AI Categorized"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 20,
                    right: 20,
                    background: theme.palette.custom.theoAI,
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: "12px",
                    zIndex: 1,
                  }}
                />
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      fontSize: "16px",
                      fontWeight: 600,
                      color: theme.palette.custom.titleGray,
                    }}
                  >
                    Category & Subcategory Breakdown
                  </Typography>

                  {/* Build hierarchical structure */}
                  {(() => {
                    // Group subcategories by category
                    const categoryMap: Record<string, Array<{ subcategory: string; count: number }>> = {};
                    
                    Object.entries(analytics.tickets_by_category_and_subcategory).forEach(([key, count]) => {
                      const [category, subcategory] = key.split(' > ');
                      if (category && subcategory) {
                        if (!categoryMap[category]) {
                          categoryMap[category] = [];
                        }
                        categoryMap[category].push({ subcategory, count });
                      }
                    });

                    // Sort categories by total tickets
                    const sortedCategories = Object.entries(categoryMap)
                      .map(([category, subcategories]) => ({
                        category,
                        subcategories: subcategories.sort((a, b) => b.count - a.count),
                        totalTickets: subcategories.reduce((sum, sub) => sum + sub.count, 0),
                      }))
                      .sort((a, b) => b.totalTickets - a.totalTickets);

                    return (
                      <Box>
                        {sortedCategories.map(({ category, subcategories, totalTickets }, index) => (
                          <Accordion
                            key={category}
                            defaultExpanded={index < 3}
                            sx={{
                              mb: 1,
                              boxShadow: "none",
                              border: "1px solid #E2E8F0",
                              borderRadius: "6px",
                              "&:before": { display: "none" },
                              "&.Mui-expanded": {
                                margin: "0 0 8px 0",
                              },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                background: "#F8FAFC",
                                borderRadius: "6px",
                                "&.Mui-expanded": {
                                  borderBottomLeftRadius: 0,
                                  borderBottomRightRadius: 0,
                                },
                                "&:hover": {
                                  background: "#F1F5F9",
                                },
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.custom.titleGray }}>
                                  {category}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                  <Chip
                                    label={`${subcategories.length} subcategories`}
                                    size="small"
                                    sx={{
                                      background: "#DBEAFE",
                                      color: "#1E40AF",
                                      fontSize: "11px",
                                    }}
                                  />
                                  <Chip
                                    label={`${totalTickets} tickets`}
                                    size="small"
                                    sx={{
                                      background: theme.palette.custom.theoAI,
                                      color: "#FFFFFF",
                                      fontWeight: 600,
                                    }}
                                  />
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 2 }}>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600, color: theme.palette.custom.titleGray }}>
                                        Subcategory
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.custom.titleGray }}>
                                        Tickets
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.custom.titleGray, width: "150px" }}>
                                        Percentage
                                      </TableCell>
                                      <TableCell sx={{ width: "200px" }}>
                                        {/* Visual bar */}
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {subcategories.map(({ subcategory, count }) => {
                                      const percentage = totalTickets > 0 ? (count / totalTickets) * 100 : 0;
                                      return (
                                        <TableRow
                                          key={subcategory}
                                          sx={{
                                            "&:hover": {
                                              background: "#F8FAFC",
                                            },
                                          }}
                                        >
                                          <TableCell>
                                            <Typography variant="body2" sx={{ pl: 2 }}>
                                              {subcategory}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Chip
                                              label={count}
                                              size="small"
                                              sx={{
                                                background: "#E0E7FF",
                                                color: "#4338CA",
                                                fontWeight: 600,
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2" color="text.secondary">
                                              {percentage.toFixed(1)}%
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Box
                                              sx={{
                                                width: "100%",
                                                height: "8px",
                                                background: "#E2E8F0",
                                                borderRadius: "4px",
                                                overflow: "hidden",
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  width: `${percentage}%`,
                                                  height: "100%",
                                                  background: theme.palette.custom.theoAI,
                                                  transition: "width 0.3s ease",
                                                }}
                                              />
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* All Subcategories Overview Chart */}
            {analytics.tickets_by_subcategory && Object.keys(analytics.tickets_by_subcategory).length > 0 && (
              <Card
                sx={{
                  mb: 3,
                  background: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  border: "1px solid #F1F5F9",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontSize: "16px",
                      fontWeight: 600,
                      color: theme.palette.custom.titleGray,
                    }}
                  >
                    All Subcategories Overview
                  </Typography>
                  <BarChart
                    xAxis={[
                      {
                        scaleType: "band",
                        data: Object.keys(analytics.tickets_by_subcategory).slice(0, 15),
                        tickLabelStyle: {
                          angle: -45,
                          textAnchor: "end",
                          fontSize: 10,
                        },
                      },
                    ]}
                    series={[
                      {
                        data: Object.values(analytics.tickets_by_subcategory).slice(0, 15),
                        color: theme.palette.custom.chartOrange,
                        label: "Tickets",
                      },
                    ]}
                    height={400}
                  />
                  {Object.keys(analytics.tickets_by_subcategory).length > 15 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                      Showing top 15 subcategories. Total: {Object.keys(analytics.tickets_by_subcategory).length} subcategories
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Top Accounts */}
            {Object.keys(analytics.tickets_by_account).length > 0 && (
              <Card
                sx={{
                  background: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  border: "1px solid #F1F5F9",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontSize: "16px",
                      fontWeight: 600,
                      color: theme.palette.custom.titleGray,
                    }}
                  >
                    Top Accounts by Ticket Volume
                  </Typography>
                  <Box>
                    {Object.entries(analytics.tickets_by_account)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 10)
                      .map(([account, count]) => (
                        <Box
                          key={account}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1.5,
                            borderBottom: "1px solid #F1F5F9",
                            "&:last-child": { borderBottom: "none" },
                          }}
                        >
                          <Typography variant="body2">{account}</Typography>
                          <Chip
                            label={count}
                            size="small"
                            sx={{
                              background: "#F1F5F9",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default QueueAnalytics;
